import React from 'react';
import { withStyles } from 'tss-react/mui';
import AbstractDialogTab from '../../../base/dialog/components/web/AbstractDialogTab';
import { translate } from '../../../base/i18n/functions';
import Checkbox from '../../../base/ui/components/web/Checkbox';
import { isMobileBrowser } from "../../../base/environment/utils";
import Switch from "../../../base/ui/components/web/Switch";
import clsx from "clsx";
const styles = (theme) => {
    return {
        container: {
            display: 'flex',
            width: '100%',
            flexDirection: 'column'
        },
        tabTitleContainer: {
            '&.is-mobile': {
                display: 'flex',
                flexDirection: 'column',
                padding: '0 16px',
            }
        },
        tabTitle: {
            '&.is-mobile': {
                padding: '16px 0 6px 0',
                fontFamily: 'Lato Bold',
                fontWeight: 'normal',
                fontSize: '20px',
                lineHeight: '28px',
                letterSpacing: '-0.3px',
                color: 'rgba(255, 255, 255, 0.8)',
            }
        },
        tabTitleDivider: {
            '&.is-mobile': {
                width: '100%',
                margin: '15px 0 20px 0',
                borderTop: '1px dashed rgba(255, 255, 255, 0.1)'
            }
        },
        column: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            '&:first-child:not(:last-child)': {
                marginBottom: '24px'
            },
            '&.is-mobile': {
                padding: '0 16px',
                gap: '4px',
                '&:first-child:not(:last-child)': {
                    marginBottom: 0
                },
                '&:last-child': {
                    marginBottom: '24px'
                }
            },
        },
        title: {
            fontFamily: 'Lato Bold',
            fontWeight: 'normal',
            fontSize: '15px',
            lineHeight: '22px',
            color: 'rgba(255, 255, 255, 0.75)',
            marginBottom: '16px',
            '&.is-mobile': {
                fontSize: '18px',
                lineHeight: '26px',
                color: 'rgba(255, 255, 255, 0.70)',
                letterSpacing: '-0.25px',
                marginTop: 0,
                marginBottom: '8px',
            }
        },
        controlRowContainer: {
            '&.is-mobile': {
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '7px 0 6px 0',
            },
            '&> label': {
                '&.is-mobile': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontFamily: 'Lato Regular',
                    fontWeight: 'normal',
                    fontSize: '18px',
                    lineHeight: '26px',
                    letterSpacing: '-0.25px',
                },
            },
        },
        checkbox: {
            padding: 0,
            marginBottom: '12px'
        },
        text: {
            lineHeight: '18px',
        },
    };
};
/**
 * React {@code Component} for modifying the local user's sound settings.
 *
 * @augments Component
 */
class NotificationsTab extends AbstractDialogTab {
    /**
     * Initializes a new {@code SoundsTab} instance.
     *
     * @param {IProps} props - The React {@code Component} props to initialize
     * the new {@code SoundsTab} instance with.
     */
    constructor(props) {
        super(props);
        // Bind event handlers so they are only bound once for every instance.
        this._onChange = this._onChange.bind(this);
        this._onEnabledNotificationsChanged = this._onEnabledNotificationsChanged.bind(this);
    }
    /**
     * Changes a sound setting state.
     *
     * @param {Object} e - The key event to handle.
     *
     * @returns {void}
     */
    _onChange({ target }) {
        super._onChange({ [target.name]: target.checked });
    }
    /**
     * Changes a sound setting state.
     *
     * @param {String} name - The key event to handle.
     * @param {Boolean} checked - The state event to handle.
     *
     * @returns {void}
     */
    _onChangeSwitch(name, checked) {
        super._onChange({ [name]: checked });
    }
    /**
     * Callback invoked to select if the given type of
     * notifications should be shown.
     *
     * @param {Object} e - The key event to handle.
     * @param {string} type - The type of the notification.
     *
     * @returns {void}
     */
    _onEnabledNotificationsChanged({ target: { checked } }, type) {
        super._onChange({
            enabledNotifications: {
                ...this.props.enabledNotifications,
                [type]: checked
            }
        });
    }
    /**
     * Callback invoked to select if the given type of
     * notifications should be shown.
     *
     * @param {string} type - The type of the notification.
     * @param {Boolean} checked - The state event to handle.
     *
     * @returns {void}
     */
    _onEnabledNotificationsChangedSwitch(type, checked) {
        super._onChange({
            enabledNotifications: {
                ...this.props.enabledNotifications,
                [type]: checked
            }
        });
    }
    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        const { disabledSounds, enabledNotifications, showNotificationsSettings, showSoundsSettings, soundsIncomingMessage, soundsParticipantJoined, soundsParticipantKnocking, soundsParticipantLeft, soundsTalkWhileMuted, soundsReactions, enableReactions, moderatorMutedSoundsReactions, t } = this.props;
        const classes = withStyles.getClasses(this.props);
        const isMobile = isMobileBrowser();
        if (isMobile) {
            return (React.createElement("div", { className: classes.container, key: 'sounds' },
                React.createElement("div", { className: clsx(classes.tabTitleContainer, 'is-mobile') },
                    React.createElement("div", { className: clsx(classes.tabTitle, 'is-mobile') }, t('settings.notifications')),
                    React.createElement("div", { className: clsx(classes.tabTitleDivider, 'is-mobile') })),
                showSoundsSettings && (React.createElement("div", { className: clsx(classes.column, 'is-mobile') },
                    React.createElement("h2", { className: clsx(classes.title, 'is-mobile') }, t('settings.playSounds')),
                    React.createElement("div", { className: clsx(classes.controlRowContainer, 'is-mobile') },
                        React.createElement("label", { htmlFor: 'soundsIncomingMessage-switch', className: 'is-mobile' }, t('settings.incomingMessage')),
                        React.createElement(Switch, { checked: soundsIncomingMessage && !disabledSounds.includes('INCOMING_MSG_SOUND'), id: 'soundsIncomingMessage-switch', onChange: checked => this._onChangeSwitch("soundsIncomingMessage", checked ?? false) })),
                    React.createElement("div", { className: clsx(classes.controlRowContainer, 'is-mobile') },
                        React.createElement("label", { htmlFor: 'soundsParticipantJoined-switch', className: 'is-mobile' }, t('settings.participantJoined')),
                        React.createElement(Switch, { checked: soundsParticipantJoined
                                && !disabledSounds.includes('PARTICIPANT_JOINED_SOUND'), id: 'soundsParticipantJoined-switch', onChange: checked => this._onChangeSwitch("soundsParticipantJoined", checked ?? false) })),
                    React.createElement("div", { className: clsx(classes.controlRowContainer, 'is-mobile') },
                        React.createElement("label", { htmlFor: 'soundsParticipantLeft-switch', className: 'is-mobile' }, t('settings.participantLeft')),
                        React.createElement(Switch, { checked: soundsParticipantLeft && !disabledSounds.includes('PARTICIPANT_LEFT_SOUND'), id: 'soundsParticipantLeft-switch', onChange: checked => this._onChangeSwitch("soundsParticipantLeft", checked ?? false) })),
                    React.createElement("div", { className: clsx(classes.controlRowContainer, 'is-mobile') },
                        React.createElement("label", { htmlFor: 'soundsTalkWhileMuted-switch', className: 'is-mobile' }, t('settings.talkWhileMuted')),
                        React.createElement(Switch, { checked: soundsTalkWhileMuted && !disabledSounds.includes('TALK_WHILE_MUTED_SOUND'), id: 'soundsTalkWhileMuted-switch', onChange: checked => this._onChangeSwitch("soundsTalkWhileMuted", checked ?? false) })),
                    React.createElement("div", { className: clsx(classes.controlRowContainer, 'is-mobile') },
                        React.createElement("label", { htmlFor: 'soundsParticipantKnocking-switch', className: 'is-mobile' }, t('settings.participantKnocking')),
                        React.createElement(Switch, { checked: soundsParticipantKnocking
                                && !disabledSounds.includes('KNOCKING_PARTICIPANT_SOUND'), id: 'soundsParticipantKnocking-switch', onChange: checked => this._onChangeSwitch("soundsParticipantKnocking", checked ?? false) })))),
                showNotificationsSettings && (React.createElement("div", { className: clsx(classes.column, 'is-mobile') },
                    showSoundsSettings && (React.createElement("div", { className: clsx(classes.tabTitleDivider, 'is-mobile') })),
                    React.createElement("h2", { className: clsx(classes.title, 'is-mobile') }, t('notify.displayNotifications')),
                    Object.keys(enabledNotifications).map(key => (React.createElement("div", { className: clsx(classes.controlRowContainer, 'is-mobile') },
                        React.createElement("label", { htmlFor: `show-${key}-switch`, className: 'is-mobile' }, t(key)),
                        React.createElement(Switch, { checked: Boolean(enabledNotifications[key]), id: `show-${key}-switch`, 
                            /* eslint-disable-next-line react/jsx-no-bind */
                            onChange: checked => this._onEnabledNotificationsChangedSwitch(key, checked ?? false) }))))))));
        }
        return (React.createElement("div", { className: classes.container, key: 'sounds' },
            showSoundsSettings && (React.createElement("div", { className: classes.column },
                React.createElement("h2", { className: classes.title }, t('settings.playSounds')),
                React.createElement(Checkbox, { checked: soundsIncomingMessage && !disabledSounds.includes('INCOMING_MSG_SOUND'), className: classes.checkbox, classNameText: classes.text, disabled: disabledSounds.includes('INCOMING_MSG_SOUND'), label: t('settings.incomingMessage'), name: 'soundsIncomingMessage', onChange: this._onChange }),
                React.createElement(Checkbox, { checked: soundsParticipantJoined
                        && !disabledSounds.includes('PARTICIPANT_JOINED_SOUND'), className: classes.checkbox, classNameText: classes.text, disabled: disabledSounds.includes('PARTICIPANT_JOINED_SOUND'), label: t('settings.participantJoined'), name: 'soundsParticipantJoined', onChange: this._onChange }),
                React.createElement(Checkbox, { checked: soundsParticipantLeft && !disabledSounds.includes('PARTICIPANT_LEFT_SOUND'), className: classes.checkbox, classNameText: classes.text, disabled: disabledSounds.includes('PARTICIPANT_LEFT_SOUND'), label: t('settings.participantLeft'), name: 'soundsParticipantLeft', onChange: this._onChange }),
                React.createElement(Checkbox, { checked: soundsTalkWhileMuted && !disabledSounds.includes('TALK_WHILE_MUTED_SOUND'), className: classes.checkbox, classNameText: classes.text, disabled: disabledSounds.includes('TALK_WHILE_MUTED_SOUND'), label: t('settings.talkWhileMuted'), name: 'soundsTalkWhileMuted', onChange: this._onChange }),
                React.createElement(Checkbox, { checked: soundsParticipantKnocking
                        && !disabledSounds.includes('KNOCKING_PARTICIPANT_SOUND'), className: classes.checkbox, classNameText: classes.text, disabled: disabledSounds.includes('KNOCKING_PARTICIPANT_SOUND'), label: t('settings.participantKnocking'), name: 'soundsParticipantKnocking', onChange: this._onChange }))),
            showNotificationsSettings && (React.createElement("div", { className: classes.column },
                React.createElement("h2", { className: classes.title }, t('notify.displayNotifications')),
                Object.keys(enabledNotifications).map(key => (React.createElement(Checkbox, { checked: Boolean(enabledNotifications[key]), className: classes.checkbox, classNameText: classes.text, key: key, label: t(key), name: `show-${key}`, 
                    /* eslint-disable-next-line react/jsx-no-bind */
                    onChange: e => this._onEnabledNotificationsChanged(e, key) })))))));
    }
}
export default withStyles(translate(NotificationsTab), styles);
